import { useEffect, useRef, useState } from 'react';
import { Button, Stack } from '@mui/material';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import FloatingLabelDateInputLayout from 'framework/ui/layout/form/input/date/FLDateInputLayout';
import { DATE_FORMAT, DEFAULT_TIMEZONE } from 'framework/utils/hooks/useDateService';
import type { FilterValue } from '../contracts';

export interface FilterBarDateRangeFilterConfig {
  type: 'dateRange';
  id: string;
  source: string;
  label: string;
}

const INVALID_DATE_MESSAGE = 'Enter a valid date';
const INVERTED_RANGE_MESSAGE = 'Cannot be earlier than the start of the range';

const DATE_INPUT_SX = { width: { xs: '100%', sm: 150 } };

const parseDate = (value: string) => moment(value, DATE_FORMAT.INPUT_VALUE, true);
const isInvalidDate = (value: string) => Boolean(value && !parseDate(value).isValid());
const getCalendarBoundary = (value: string) =>
  value && !isInvalidDate(value) ? momentTimezone.tz(value, DATE_FORMAT.INPUT_VALUE, DEFAULT_TIMEZONE) : undefined;

interface DateRangeFilterEditorProps {
  filter: FilterBarDateRangeFilterConfig;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  onClose: () => void;
}

const DateRangeFilterEditor = ({ filter, value, onChange, onClose }: DateRangeFilterEditorProps) => {
  const formRef = useRef<HTMLFormElement>(null);
  const [from, setFrom] = useState(Array.isArray(value) ? (value[0] ?? '') : '');
  const [to, setTo] = useState(Array.isArray(value) ? (value[1] ?? '') : '');

  useEffect(() => {
    formRef.current?.querySelector('input')?.focus();
  }, []);

  const fromInvalid = isInvalidDate(from);
  const toInvalid = isInvalidDate(to);
  const isInverted = Boolean(from && to && !fromInvalid && !toInvalid && parseDate(to).isBefore(parseDate(from)));
  const hasErrors = fromInvalid || toInvalid || isInverted;

  return (
    <Stack
      ref={formRef}
      component="form"
      aria-label={`${filter.label} filter`}
      spacing={2}
      sx={{ p: 2, minWidth: 260 }}
      onSubmit={event => {
        event.preventDefault();
        if (hasErrors) return;
        onChange(from || to ? [from, to] : null);
        onClose();
      }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
        <FloatingLabelDateInputLayout
          source={`${filter.id}-from`}
          dateFormat={DATE_FORMAT.INPUT_VALUE}
          displayTimeZone={DEFAULT_TIMEZONE}
          closeOnSelect
          sx={DATE_INPUT_SX}
          maxDate={getCalendarBoundary(to)}
          errorMessages={fromInvalid ? [INVALID_DATE_MESSAGE] : []}
          label={`${filter.label} from`}
          value={from}
          onChangeCallback={event => setFrom(event.target.value)}
        />
        <FloatingLabelDateInputLayout
          source={`${filter.id}-to`}
          dateFormat={DATE_FORMAT.INPUT_VALUE}
          displayTimeZone={DEFAULT_TIMEZONE}
          closeOnSelect
          sx={DATE_INPUT_SX}
          minDate={getCalendarBoundary(from)}
          errorMessages={toInvalid ? [INVALID_DATE_MESSAGE] : isInverted ? [INVERTED_RANGE_MESSAGE] : []}
          label={`${filter.label} to`}
          value={to}
          onChangeCallback={event => setTo(event.target.value)}
        />
      </Stack>
      <Stack direction="row" spacing={2}>
        <Button type="submit" variant="outlined" disabled={hasErrors}>
          Update
        </Button>
        <Button type="button" onClick={onClose}>
          Cancel
        </Button>
      </Stack>
    </Stack>
  );
};

export default DateRangeFilterEditor;
